// Turn text (via a font) or a validated SVG into normalized, centered,
// y-down THREE.Shapes sized to LIMITS.pendantMaxSize, ready for <lume-shape>.
import {Shape, Path, ShapePath, Vector2} from 'three'
import {SVGLoader} from 'three/examples/jsm/loaders/SVGLoader.js'
import opentype from 'opentype.js'
import {LIMITS} from '../imports/config.js'
import {validateSvg, SvgError} from './svg-sanitize.js'

/** @typedef {import('opentype.js').Font} Font */

/** @type {Map<string, Promise<Font>>} */
const fontCache = new Map()

/**
 * Fetch + parse a font (cached per URL). Failures are NOT cached and are retried, so one
 * transient network/deploy hiccup cannot poison every later preview.
 * @param {string} url
 * @returns {Promise<Font>}
 */
export function loadFont(url) {
  const cached = fontCache.get(url)
  if (cached) return cached
  const p = fetchFont(url).catch(e => {
    fontCache.delete(url)
    throw e
  })
  fontCache.set(url, p)
  return p
}

/**
 * @param {string} url
 * @param {number} [attempts]
 * @returns {Promise<Font>}
 */
async function fetchFont(url, attempts = 3) {
  /** @type {unknown} */
  let last
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await fetch(url, {cache: i === 0 ? 'default' : 'reload'})
      if (!r.ok) throw new Error(`font ${url}: HTTP ${r.status}`)
      // Meteor answers unknown paths with the app HTML (200), which would otherwise surface as an opaque parse error.
      if ((r.headers.get('content-type') || '').includes('text/html'))
        throw new Error(`font ${url}: got HTML, not a font`)
      return opentype.parse(await r.arrayBuffer())
    } catch (e) {
      last = e
      await new Promise(res => setTimeout(res, 300 * (i + 1)))
    }
  }
  throw last
}

/**
 * Glyph outlines of `text` as three.js shapes (y-down font units, size 100).
 * @param {Font} font
 * @param {string} text
 * @returns {Shape[]}
 */
export function shapesFromText(font, text) {
  const path = font.getPath(text, 0, 0, 100)
  const sp = new ShapePath()
  for (const c of path.commands) {
    if (c.type === 'M') sp.moveTo(c.x, c.y)
    else if (c.type === 'L') sp.lineTo(c.x, c.y)
    else if (c.type === 'C') sp.bezierCurveTo(c.x1, c.y1, c.x2, c.y2, c.x, c.y)
    else if (c.type === 'Q') sp.quadraticCurveTo(c.x1, c.y1, c.x, c.y)
  }
  return SVGLoader.createShapes(sp)
}

/**
 * Geometry of a validated user SVG (filled shapes only).
 * @param {string} source raw SVG text (validated here; throws SvgError)
 * @returns {Shape[]}
 */
export function shapesFromSvg(source) {
  validateSvg(source)
  const data = new SVGLoader().parse(source)
  /** @type {Shape[]} */
  const shapes = []
  for (const p of data.paths) {
    if (/** @type {any} */ (p.userData)?.style?.fill === 'none') continue
    for (const s of SVGLoader.createShapes(p)) shapes.push(s)
  }
  if (!shapes.length)
    throw new SvgError(
      'No filled shapes found. Outline-only (stroke) artwork must be converted to filled shapes first.',
    )
  if (shapes.length > LIMITS.svgMaxShapes)
    throw new SvgError(`Too many separate shapes (${shapes.length}; max ${LIMITS.svgMaxShapes}).`)
  return shapes
}

/**
 * @param {Vector2} p
 * @param {number} s scale
 * @param {number} cx center x
 * @param {number} cy center y
 */
const pt = (p, s, cx, cy) => new Vector2((p.x - cx) * s, (p.y - cy) * s)

/**
 * Flatten curves, center on origin, scale longest side to maxSize.
 * @param {Shape[]} shapes
 * @param {number} [maxSize] longest side limit
 * @param {number} [maxHeight] height limit (keeps one-letter / short words from becoming giant slabs)
 * @returns {import('../types/pendant.js').NormalizedShapes}
 */
export function normalizeShapes(shapes, maxSize = LIMITS.pendantMaxSize, maxHeight = Infinity) {
  const flat = shapes.map(s => s.extractPoints(10))
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const f of flat)
    for (const p of f.shape) {
      minX = Math.min(minX, p.x)
      maxX = Math.max(maxX, p.x)
      minY = Math.min(minY, p.y)
      maxY = Math.max(maxY, p.y)
    }
  const w = maxX - minX
  const h = maxY - minY
  if (!isFinite(w) || !isFinite(h) || w < 1e-6 || h < 1e-6) throw new SvgError('The artwork has no visible size.')
  const s = Math.min(maxSize / Math.max(w, h), maxHeight / h)
  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2
  const out = flat.map(f => {
    const shape = new Shape(f.shape.map(p => pt(p, s, cx, cy)))
    shape.holes = f.holes.map(hole => new Path(hole.map(p => pt(p, s, cx, cy))))
    return shape
  })
  return {shapes: out, width: w * s, height: h * s}
}
